import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PersonIcon from '@mui/icons-material/Person';
import { Badge, Box, Button, List, ListItem, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import { GridColDef, GridRenderCellParams, GridSelectionModel, GridToolbarExport } from '@mui/x-data-grid-pro';
import React, { useEffect, useState } from 'react';
import Loader from 'src/components/common/Loader';
import { useTab } from 'src/components/hooks/useTab';
import { archLevel } from 'src/data/config';
import { useLazyGetStudyAllCRFVisitsQuery } from 'src/services/SubjectTrackingDetails';
import { setMessage } from 'src/store/Message';
import { setPath } from 'src/store/Navigation';
import { setBackButtonPTN } from 'src/store/SelectedIntrialPatient';
import { setSelectedSubject, setSubjectInfo } from 'src/store/SelectedSubject';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { getBadgeClassName, getStatusMatchedIcon } from './StatusMatchedIcon';
import { useDataReview } from './useDataReview';
import { setPreScreenGridRowsData } from 'src/store/preScreenGridRowsData';
import { DataTable } from '@pihealth/common-ui-library';
import { usePreferedDateFormat } from 'src/util/DateFormatter';
import moment from 'moment';
interface IVisitsInfo {
  id: string;
  unit: string;
  visit_range_unit: string;
  visit_number: number;
  protocol_visit_name: string;
  planned_day: number;
  minimum_visit_range: number;
  maximum_visit_range: number;
}
const VisitLog: React.FunctionComponent<any> = () => {
  const { userPreferedDateFormat } = usePreferedDateFormat();
  const currentHierarchyLevel = useAppSelector(state => state.site.currentHierarchyLevel);
  const [visitLogGridRows, setVisitLogGridRows] = useState([]);
  const [maxNumberOfVisits, setMaxNumberOfVisits] = useState([] as IVisitsInfo[]);
  const [pageSize, setPageSize] = React.useState(10);
  const [rowsCount, setRowsCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [selectionModel, setSelectionModel] = React.useState<GridSelectionModel>([]);
  const [currentMenuID, setCurrentMenuID] = useState('');
  const studyId: any = useAppSelector(state => state.site.selectedStudy);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const selectedSite = useAppSelector(state => state.site.selectedSite);
  const isHierarchyLevelClosed: boolean = useAppSelector(state => state.site.isHierarchyLevelClosed);
  const [isGridLoading, setIsGridLoading] = useState(false);
  const { dataReviewData, dataReviewDataError, dataReviewDataIsLoading, getDataReviewData } = useDataReview();
  const [getAllCRFVisits, { isLoading: getAllCRFVisitsIsLoading }] = useLazyGetStudyAllCRFVisitsQuery();
  const [filterObject, setFilterObject] = useState<any>();

  const getMaxCRFVisits = async () => {
    setIsGridLoading(true);
    const visitsResponse: any = await getAllCRFVisits({
      study_id: studyId,
      site_id: selectedSite,
      country: selectedCountry
    });
    if (visitsResponse.isSuccess) {
      const visitsInfo = visitsResponse.data.map((visit: any) => {
        return {
          id: visit.id,
          unit: visit.unit,
          visit_range_unit: visit.visit_range_unit,
          visit_number: visit.visit_number,
          protocol_visit_name: visit.protocol_visit_name,
          planned_day: visit.planned_day,
          minimum_visit_range: visit.minimum_visit_range,
          maximum_visit_range: visit.maximum_visit_range
        };
      });
      setMaxNumberOfVisits(visitsInfo);
    } else if (visitsResponse.isError) {
      setMaxNumberOfVisits([]);
      dispatch(
        setMessage({
          msgDetails: 'Error fetching visits',
          severity: 'error'
        })
      );
    }
    setIsGridLoading(false);
  };
  const getDataReviewDataFunc = async (from: number, size: number) => {
    const response: any = await getDataReviewData({
      site_id: selectedSite,
      study_id: studyId,
      country: selectedCountry,
      data: filterObject,
      application: archLevel === 'sponsor' ? process.env.REACT_APP_NAME : process.env.REACT_APP_NAME_TEMP,
      from,
      size
    });
    if (response?.isSuccess) {
      const tempRowsData: any = [];
      if (response?.data?.source_data_review.length > 0) {
        let idValue = 1;
        response?.data?.source_data_review?.map((subjectInfo: any) => {
          const subjectFound = false;
          if (!subjectFound) {
            let tempVisitRow: any = {};
            if (subjectInfo?.visits?.length > 0) {
              subjectInfo.visits.map((visit: any) => {
                const tempCRFs: any = [];
                if (visit.crfs.length > 0) {
                  visit.crfs.map((eachCRF: any) => {
                    tempCRFs.push({
                      oid: eachCRF.crf_OID,
                      status: eachCRF.status,
                      query_status: eachCRF.query_status,
                      custom_name: eachCRF?.custom_name
                    });
                  });
                }
                tempVisitRow = {
                  ...tempVisitRow,
                  [`v_${visit.visit_sequence}`]: visit.visit_name,
                  [`vsd_${visit.visit_sequence}`]: visit.visit_start_date,
                  [`vcrfs_${visit.visit_sequence}`]: tempCRFs,
                  [`vId_${visit.visit_sequence}`]: visit.visit_id
                };
              });
            }
            tempRowsData.push({
              id: idValue,
              sl_no: subjectInfo.subject.sl_no,
              subject_status: subjectInfo.subject.subject_status,
              site_id: subjectInfo.site_id,
              country: subjectInfo.country,
              subject_id: subjectInfo.subject.subject_id,
              patient_identifier: subjectInfo.subject.patient_identifier,
              prescreening_id: subjectInfo.subject.prescreening_id,
              ehr_facility_id: subjectInfo.subject.ehr_facility_id,
              crf_activated: subjectInfo.subject.crf_activated,
              ...tempVisitRow
            });
            idValue = idValue + 1;
          }
        });
      }
      setVisitLogGridRows(tempRowsData);
      setRowsCount(dataReviewData?.total_records);
    }
  };
  useEffect(() => {
    if (!dataReviewDataIsLoading && (pageSize === 10 || pageSize === 20 || pageSize === 30)) {
      if (pageNumber * pageSize > rowsCount - 1) {
        setPageNumber(0);
        getDataReviewDataFunc(0, pageSize);
      } else {
        getDataReviewDataFunc(pageNumber * pageSize, pageSize);
      }
    }
  }, [pageNumber, pageSize, filterObject]);
  useEffect(() => {
    (async () => {
      if (studyId !== null) {
        if (!maxNumberOfVisits.length) {
          await getMaxCRFVisits();
        }
        getDataReviewDataFunc(0, pageSize);
      } else {
        setVisitLogGridRows([]);
      }
    })();
  }, [studyId, selectedCountry, selectedSite, isHierarchyLevelClosed]);
  useEffect(() => {
    if (currentHierarchyLevel === null) {
      setMaxNumberOfVisits([]);
    }
  }, [currentHierarchyLevel]);

  useEffect(() => {
    if (dataReviewDataError) {
      setVisitLogGridRows([]);
      setRowsCount(0);
    }
  }, [dataReviewDataError]);
  useEffect(() => {
    if (dataReviewDataIsLoading) {
      setIsGridLoading(true);
    } else {
      setIsGridLoading(false);
    }
  }, [dataReviewDataIsLoading]);

  const handleSubjectSelect = (row: any) => {
    const subjectInfo = {
      subject_id: row.subject_id,
      visit_type: '',
      selected_crf: '',
      patient_identifier: row.patient_identifier,
      prescreening_id: row.prescreening_id,
      ehr_facility_id: row.ehr_facility_id
    };
    dispatch(setSubjectInfo(subjectInfo));
    dispatch(setSelectedSubject(row.subject_id));
    dispatch(setPreScreenGridRowsData({ ...row, pre_screen_id: row.prescreening_id, sl_no: row.sl_no }));
    navigateToSubjectTracking();
  };
  const handleCRFClick = (
    subjectID: string,
    visitName: string,
    crfOID: string,
    patientIdentifier: string,
    preScreeningId: string,
    ehrFacilityId: string,
    visit_id: string
  ) => {
    const subjectInfo = {
      subject_id: subjectID,
      visit_type: visitName,
      visit_id,
      selected_crf: crfOID,
      patient_identifier: patientIdentifier,
      prescreening_id: preScreeningId,
      ehr_facility_id: ehrFacilityId
    };
    dispatch(setSubjectInfo(subjectInfo));
    dispatch(setSelectedSubject(subjectID));
    dispatch(setBackButtonPTN(true));
    navigateToSubjectTracking();
  };

  const { setTabVal, tabsData } = useTab();
  const dispatch = useAppDispatch();
  const navigateToSubjectTracking = () => {
    //Adding : separated value to decide which grid data we redirecting from
    dispatch(setPath('dataReview:visitLog'));
    let currentTabinfo: any = {};
    if (archLevel === 'site') {
      currentTabinfo = tabsData.find((item: any) => item.key === 'patientTrialNoteBook');
    } else {
      currentTabinfo = tabsData.find((item: any) => item.key === 'subjectTracking');
    }
    if (currentTabinfo?.index) {
      setTabVal(currentTabinfo.index);
    }
  };
  const getVisitsColumns = () => {
    const visitColumns: any = [];
    maxNumberOfVisits.length &&
      maxNumberOfVisits.forEach((visit, index) => {
        const colNo: number = index + 1;
        visitColumns.push({
          field: `v_${colNo}`,
          headerName: `Visit ${colNo}`,
          minWidth: (visit.protocol_visit_name.length > 10 ? visit.protocol_visit_name.length : 10) * 7 + 90,
          renderCell: (params: GridRenderCellParams<any>) => {
            if (params.row[`v_${colNo}`]) {
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 0.75 }}>
                  <List component="nav" aria-label={`Visit-${colNo}`} className="visitDDinRow ">
                    <ListItem
                      id={`Visit-${colNo}`}
                      aria-haspopup="listbox"
                      aria-controls="lock-menu"
                      aria-label={`Visit-${colNo}`}
                      aria-expanded={open ? 'true' : undefined}
                      onClick={e => {
                        setCurrentMenuID(`v-${colNo}-${params.row.subject_id}`);
                        handleClickListItem(e);
                      }}
                    >
                      <ListItemIcon>
                        <EventAvailableIcon fontSize="large" color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={params.row[`v_${colNo}`]}
                        secondary={
                          params.row[`vsd_${colNo}`]
                            ? moment(params.row[`vsd_${colNo}`]).format(`${userPreferedDateFormat.toUpperCase()}`)
                            : ''
                        }
                      />
                      <ListItemIcon>
                        <ArrowDropDownIcon />
                      </ListItemIcon>
                    </ListItem>
                  </List>
                  <Menu
                    sx={{ ml: 1 }}
                    id={`v-${colNo}-${params.row.subject_id}`}
                    anchorEl={anchorEl}
                    open={open && currentMenuID === `v-${colNo}-${params.row.subject_id}`}
                    onClose={handleClose}
                    MenuListProps={{
                      'aria-labelledby': 'Visit one',
                      role: 'listbox'
                    }}
                    disableScrollLock
                  >
                    {params.row[`vcrfs_${colNo}`].map((crf: any) => {
                      return (
                        <MenuItem
                          key={crf.oid}
                          onClick={() =>
                            params.row.crf_activated
                              ? handleCRFClick(
                                  params.row.subject_id,
                                  params.row[`v_${colNo}`],
                                  crf.oid,
                                  params.row.patient_identifier,
                                  params.row.prescreening_id,
                                  params.row.ehr_facility_id,
                                  params.row[`vId_${colNo}`]
                                )
                              : dispatch(
                                  setMessage({
                                    msgDetails: `CRF’s are not activated for this patient. You will be able to access CRF’s once they are activated in Patient Trial Notebook.`,
                                    severity: 'warning'
                                  })
                                )
                          }
                          sx={{
                            minWidth: `${
                              (params.row[`v_${colNo}`].length > 10 ? params.row[`v_${colNo}`].length : 10) * 5
                            }px`
                          }}
                        >
                          <Tooltip arrow key={crf.oid} title={crf.status} placement="left" className="smalltextTooltip">
                            <Badge
                              classes={{ badge: getBadgeClassName(crf.query_status) }}
                              variant="dot"
                              invisible={crf.query_status === null}
                            >
                              {getStatusMatchedIcon(crf.status)}
                            </Badge>
                          </Tooltip>{' '}
                          &nbsp;&nbsp;
                          {crf?.custom_name?.toUpperCase() ?? crf.oid.toUpperCase()}
                        </MenuItem>
                      );
                    })}
                  </Menu>
                </Box>
              );
            } else {
              return '';
            }
          },
          valueGetter: (params: any) =>
            `${params.row[`v_${colNo}`]}, ${
              params.row[`vsd_${colNo}`]
                ? moment(params.row[`vsd_${colNo}`]).format(`${userPreferedDateFormat.toUpperCase()}`)
                : ''
            }`
        });
      });
    return visitColumns;
  };
  const visitScheduleColumns: GridColDef[] = [
    {
      field: 'subject_id',
      headerName: 'Subject Number',
      minWidth: 130,
      renderCell: (params: any) => {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 0.75 }}>
            <Button
              sx={{
                position: 'absolute'
              }}
              onClick={() => handleSubjectSelect(params.row)}
              className="userIconNameBtninTable"
            >
              <PersonIcon sx={{ color: 'rgba(0,0,0,0.5)' }} /> &nbsp;{' '}
              <span style={{ color: 'rgba(0,0,0,0.87)' }}>{params.row.subject_id}</span>
            </Button>
          </Box>
        );
      }
    },
    { field: 'sl_no', headerName: 'Sl No.', width: 70 },
    {
      field: 'site_id',
      headerName: 'Site',
      minWidth: 150
    },
    {
      field: 'country',
      headerName: 'Country',
      minWidth: 120
    },
    ...getVisitsColumns()
  ];

  // for Visit Menu
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const dataTableProps = {
    onRowsSelection: (newSelection: any) => setSelectionModel(newSelection),
    componentsProps: {
      toolbar: { showQuickFilter: false }
    },
    sx: {
      height: '84.5vh',
      width: '100%',
      '@media (max-width:1367px)': {
        height: '76.5vh'
      }
    },
    filtermode: 'server'
  };
  const definedCustomDatGridExport = (exportFields: any = {}) => {
    return (
      <GridToolbarExport
        csvOptions={{
          fileName: `Conduct Visit Log ${moment(new Date()).format(
            `${(userPreferedDateFormat.includes('/')
              ? userPreferedDateFormat.replaceAll('/', '_')
              : userPreferedDateFormat.replaceAll('-', '_')
            ).toUpperCase()} hh_mm A`
          )}`
        }}
      />
    );
  };

  const filterValues = (data: any) => {
    setFilterObject(data);
  };

  return (
    <>
      <Loader />
      <DataTable
        dataTableProps={dataTableProps}
        rows={visitLogGridRows}
        hasDefinedColumns
        columns={visitScheduleColumns}
        className="visitScheduleDatagrid"
        paging
        pageSize={pageSize}
        setPageSize={setPageSize}
        rowsCount={rowsCount}
        pageNumber={pageNumber}
        setPageNumber={setPageNumber}
        paginationMode="server"
        checkboxSelection
        selectionModel={selectionModel}
        datePreference={userPreferedDateFormat}
        loading={isGridLoading || getAllCRFVisitsIsLoading || dataReviewDataIsLoading}
        hasDefinedDataGridToolbarExport
        definedGridToolbarExport={definedCustomDatGridExport}
        filterValues={(data: any) => filterValues(data)}
      />
    </>
  );
};

export default VisitLog;
